import "../css/article.scss"
import { BackButton } from "../UI/Buttons"
import { usePage } from "../sanity/usePage"
import PT from "../sanity/PortableText"
import { urlFor } from "../sanity/imageUrl"
import ImageGallery from 'react-image-gallery'
import { LoadingSpinner } from "../UI/LoadingSpinner"
import { Reveal } from "../UI/Reveal"

export const PageContent = ({
  slug,
  fallbackTitle,
  fallbackSubtitle,
  fallbackHeroImage,
  fallbackPdfUrl,
  fallbackPdfLabel,
  fallbackGalleryItems,
  children,
  showBackButton = true,
  showHeroImage = false,
  showGallery = false,
  heroAlt = "",
  afterContent,
  portableTextWrapClassName,
  pageClassName,
  hideFallbackWhileLoading = true,
  revealGallery = true,
}) => {
  const { data, loading } = usePage(slug)
  const title = data?.title || fallbackTitle
  const subtitle = data?.subtitle || fallbackSubtitle
  const heroUrl = showHeroImage
    ? (data?.heroImage ? urlFor(data.heroImage) : fallbackHeroImage)
    : ""
  const galleryItems = showGallery
    ? (data?.gallery?.length
      ? data.gallery.map((img) => ({
        original: img.url || urlFor(img),
        thumbnail: img.url || urlFor(img),
        description: img.caption || '',
        originalAlt: img.alt || '',
        thumbnailAlt: img.alt || '',
      }))
      : (fallbackGalleryItems || []))
    : []
  const pdfUrl = data?.pdfUrl || fallbackPdfUrl
  const pdfLabel = data?.pdfLabel || fallbackPdfLabel

  // Special-case: Links page must render as clickable bulleted lists.
  // If Sanity content doesn't have list items + link markDefs (e.g. older seed), use the JSX fallback.
  const shouldUseSanityBody = (() => {
    if (!data?.body) return false
    if (slug !== 'links') return true
    const blocks = Array.isArray(data.body) ? data.body : []
    const hasBullet = blocks.some((b) => b?.listItem === 'bullet')
    const hasLinkMarkDef = blocks.some(
      (b) => Array.isArray(b?.markDefs) && b.markDefs.some((md) => md?._type === 'link' && md?.href),
    )
    return hasBullet && hasLinkMarkDef
  })()

  // Prevent "jumping" from JSX fallback → Sanity content:
  // while the Sanity request is loading, don't render the fallback content.
  if (hideFallbackWhileLoading && loading) {
    return (
      <>
        {showBackButton && <BackButton path={-1} />}
        <div className="article-container-new">
          <div className={`article center ${pageClassName || ''}`.trim()}>
            <div className="page-loading-spacer">
              <LoadingSpinner label="Seite lädt…" size="lg" />
            </div>
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      {showBackButton && <BackButton path={-1} />}
      <div className="article-container-new">
        <div className={`article center ${pageClassName || ''}`.trim()}>
          {title ? (
            <Reveal>
              <h2>
                {title}
                {subtitle ? (
                  <>
                    <br />
                    {subtitle}
                  </>
                ) : null}
              </h2>
            </Reveal>
          ) : null}

          {heroUrl ? (
            <Reveal delay={0.05}>
              <div className="article-header d-flex center">
                <img src={heroUrl} alt={heroAlt} className="profil-pic" />
              </div>
            </Reveal>
          ) : null}

          {/* For very long pages, keep the reveal threshold tiny so content doesn't stay hidden */}
          <Reveal delay={0.08} amount={0.01}>
            {shouldUseSanityBody ? (
              <PT value={data.body} wrapClassName={portableTextWrapClassName} />
            ) : (
              children
            )}
          </Reveal>

          {pdfUrl ? (
            <Reveal delay={0.1}>
              <div className="pdf-download-container">
                <a
                  href={pdfUrl}
                  className="pdf-download-link"
                  download={pdfLabel || 'Download'}
                >
                  {pdfLabel || 'PDF herunterladen'}
                </a>
              </div>
            </Reveal>
          ) : null}

          {galleryItems.length ? (
            revealGallery ? (
              <Reveal delay={0.12}>
                <div className="page-gallery-container">
                  <ImageGallery
                    items={galleryItems}
                    showPlayButton={false}
                    showFullscreenButton={false}
                  />
                </div>
              </Reveal>
            ) : (
              <div className="page-gallery-container">
                <ImageGallery
                  items={galleryItems}
                  showPlayButton={false}
                  showFullscreenButton={false}
                />
              </div>
            )
          ) : null}

          {afterContent ? <Reveal delay={0.14}>{afterContent}</Reveal> : null}
        </div>
      </div>
    </>
  )
}

